import { ragService } from './RAGService';
import type { RAGResponse } from './RAGService';
import { backendChatService } from './BackendChatService';
import { SummaryCache } from './SummaryCache';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  philosopher?: 'adam-smith' | 'karl-marx';
  citations?: RAGResponse['citations'];
  timestamp: Date;
}

export interface ChatResponse {
  philosopher: 'adam-smith' | 'karl-marx';
  content: string;
  citations: Array<{
    title: string;
    text: string;
    relevance: number;
  }>;
  suggestedReadings?: string[];
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export class PhilosopherChatService {
  private useBackendAPI: boolean;

  constructor() {
    // Use backend API by default, fall back to frontend services only if explicitly disabled
    this.useBackendAPI = import.meta.env.VITE_USE_BACKEND_API !== 'false';
    
    // Security warning if frontend API keys are still present
    if (import.meta.env.VITE_OPENAI_API_KEY || import.meta.env.VITE_PINECONE_API_KEY) {
      console.warn('⚠️ SECURITY WARNING: Frontend API keys detected. These should be removed in production.');
      console.warn('   Remove VITE_OPENAI_API_KEY and VITE_PINECONE_API_KEY from environment variables.');
      console.warn('   The backend API provides secure access to these services.');
    }
    
    console.log(`PhilosopherChatService initialized in ${this.useBackendAPI ? 'backend' : 'frontend'} mode`);
  }

  async chat(
    message: string,
    philosopher: 'adam-smith' | 'karl-marx',
    temperature: number = 0.7
  ): Promise<ChatResponse> {
    if (this.useBackendAPI) {
      return this.chatWithBackend(message, philosopher, temperature);
    } else {
      return this.chatWithFrontend(message, philosopher, temperature);
    }
  }

  private async chatWithBackend(
    message: string,
    philosopher: 'adam-smith' | 'karl-marx', 
    temperature: number
  ): Promise<ChatResponse> {
    try {
      const response = await backendChatService.chat(message, philosopher, temperature);
      
      return {
        philosopher,
        content: response.answer,
        citations: response.citations.map(citation => ({
          title: citation.title,
          text: citation.text,
          relevance: citation.relevanceScore,
        })),
        suggestedReadings: [], // Can be enhanced later
      };
    } catch (error) {
      // Check if the error is due to chat being disabled
      if (error instanceof Error && error.message.includes('CHAT_DISABLED')) {
        // Return a graceful response without attempting frontend fallback
        return {
          philosopher,
          content: `I apologize, but the chat functionality is temporarily unavailable. Please try again later or explore the static summaries of ${philosopher === 'adam-smith' ? 'Adam Smith' : 'Karl Marx'}'s works.`,
          citations: [],
          suggestedReadings: [],
        };
      }
      
      console.warn('Backend API failed, falling back to frontend services:', error);
      return this.chatWithFrontend(message, philosopher, temperature);
    }
  }

  private async chatWithFrontend(
    message: string,
    philosopher: 'adam-smith' | 'karl-marx',
    temperature: number
  ): Promise<ChatResponse> {
    try {
      // Get response from RAG pipeline
      const ragResponse = await ragService.queryPhilosopher(
        message,
        philosopher,
        temperature
      );
      
      // Format citations
      const citations = ragResponse.citations.map(citation => ({
        title: citation.metadata.work_title || 'Unknown Work',
        text: citation.text,
        relevance: citation.relevanceScore,
      }));
      
      // Suggested readings disabled - skip processing
      const suggestedReadings: string[] = [];
      
      return {
        philosopher,
        content: ragResponse.answer,
        citations,
        suggestedReadings,
        // Note: Usage tracking would need to be implemented
        // based on actual OpenAI API responses
      };
    } catch (error) {
      console.error(`Error in chat with ${philosopher}:`, error);
      
      // Check if the error is due to missing API key
      if (error instanceof Error && error.message.includes('OPENAI_API_KEY')) {
        // Return a user-friendly message instead of throwing
        return {
          philosopher,
          content: `Chat functionality is not available at this time. Please explore the static summaries of ${philosopher === 'adam-smith' ? 'Adam Smith' : 'Karl Marx'}'s works or try again later.`,
          citations: [],
          suggestedReadings: [],
        };
      }
      
      throw error;
    }
  }
  
  async getCorpusSummary(
    workTitle: string,
    philosopher: 'adam-smith' | 'karl-marx'
  ): Promise<string> {
    // Check cache first
    const cachedSummary = SummaryCache.get(workTitle, philosopher);
    if (cachedSummary) {
      console.log(`Returning cached summary for ${workTitle} by ${philosopher}`);
      return cachedSummary;
    }

    // Always use backend API for static summaries
    try {
      const response = await backendChatService.getSummary(workTitle, philosopher);
      const summary = response.summary;
      
      // Cache the successful response
      SummaryCache.set(workTitle, philosopher, summary);
      console.log(`Cached new summary for ${workTitle} by ${philosopher}`);
      
      return summary;
    } catch (error) {
      console.error('Backend API failed for summary:', error);
      throw new Error('Failed to fetch summary from backend API');
    }
  }
  
  private generateSuggestedReadings(
    citations: RAGResponse['citations'],
    philosopher: 'adam-smith' | 'karl-marx'
  ): string[] {
    const readings = new Set<string>();
    
    // Add unique work titles from citations
    citations.forEach(citation => {
      if (citation.metadata.work_title) {
        const chapter = citation.metadata.chapter ? 
          ` - ${citation.metadata.chapter}` : '';
        readings.add(`${citation.metadata.work_title}${chapter}`);
      }
    });
    
    // Add some default suggestions based on philosopher
    if (philosopher === 'adam-smith') {
      if (readings.size === 0) {
        readings.add('The Wealth of Nations - Book I');
        readings.add('The Theory of Moral Sentiments - Part I');
      }
    } else if (philosopher === 'karl-marx') {
      if (readings.size === 0) {
        readings.add('Das Kapital - Chapter 1');
        readings.add('The Communist Manifesto');
      }
    }
    
    return Array.from(readings).slice(0, 3); // Return top 3 suggestions
  }
}

export const philosopherChatService = new PhilosopherChatService();